import { CardList } from "../index";
import { useRouter } from "next/router";
import { IMetaInfo } from "@/interfaces/IMetaInfo";
import { IImageInfoDTO } from "@/interfaces/IFooterDTO";
import { useSetRecoilState } from "recoil";
import { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { IModuleFourAPIResponse } from "@/interfaces/IModuleFourDTO";
import { useDataCard, useIsMobile } from "@/hooks";
import { configColor, getPathByLink } from "@/utils";
import { InfoCard, DestinationCard, InfoAccordionCard, LaunchCard, CommerceCard, ServiceCard } from "../Card";
import { FreeMode, Keyboard } from "swiper";
import { useTranslation } from "react-i18next";

import DOMPurify from "isomorphic-dompurify";
import CustomIcon from "../CustomIcon/CustomIcon";
import ShopList from "../ShopList/ShopList";
import ShopFilter from "../ShopList/ShopFilter";
import TerminalFilterAtom from "@/atoms/TerminalFilterAtom";
import AreaFilterAtom from "@/atoms/AreaFilterAtom";
import CategoryFilterAtom from "@/atoms/CategoruFilterAtom";
import Product from "../ShopList/Product";
import RestourantList from "../ShopList/RestourantList";
import RestourantFilter from "../ShopList/RestourantFilter";
import uuid from "react-uuid";
import React from "react";

interface IModuleFourProps {
  data: IModuleFourAPIResponse;
  language: string;
}

const typeMaxSlideMap: {
  [key: string]: {
    slidesOffsetBefore: number;
    freeMode: boolean;
    maxSlideDesktop: number | "auto";
    mobileSlideNumber: number | "auto";
    mobileCentered: boolean;
    desktopCentered: boolean;
    desktopDirection: "horizontal" | "vertical" | undefined;
    mobileDirection: "horizontal" | "vertical" | undefined;
    desktopSliderEnabled: boolean;
    mobileSliderEnabled: boolean;
  };
} = {
  info: {
    maxSlideDesktop: 3,
    mobileSlideNumber: 3,
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "vertical",
    desktopSliderEnabled: false,
    mobileSliderEnabled: false,
    freeMode: false,
    slidesOffsetBefore: 0,
  },
  ecommerce: {
    maxSlideDesktop: "auto",
    mobileSlideNumber: "auto",
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "horizontal",
    desktopSliderEnabled: true,
    mobileSliderEnabled: true,
    freeMode: true,
    slidesOffsetBefore: 0,
  },
  viaggio: {
    maxSlideDesktop: 4,
    mobileSlideNumber: 1,
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "vertical",
    desktopSliderEnabled: false,
    mobileSliderEnabled: false,
    freeMode: false,
    slidesOffsetBefore: 0,
  },
  lancio: {
    maxSlideDesktop: "auto",
    mobileSlideNumber: "auto",
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "horizontal",
    desktopSliderEnabled: true,
    mobileSliderEnabled: true,
    freeMode: true,
    slidesOffsetBefore: 0,
  },
  negozio: {
    maxSlideDesktop: 4,
    mobileSlideNumber: 1,
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "vertical",
    desktopSliderEnabled: false,
    mobileSliderEnabled: false,
    freeMode: false,
    slidesOffsetBefore: 0,
  },
  servizi: {
    maxSlideDesktop: "auto",
    mobileSlideNumber: "auto",
    desktopCentered: false,
    mobileCentered: false,
    desktopDirection: "horizontal",
    mobileDirection: "horizontal",
    desktopSliderEnabled: true,
    mobileSliderEnabled: true,
    freeMode: true,
    slidesOffsetBefore: 0,
  },
};

const ModuleFourWrapper: React.FC<IModuleFourProps> = ({ data, language }: IModuleFourProps) => {
  const moduleData = data;

  const { i18n } = useTranslation();
  const lang = language ? language : i18n.language;
  const [isMobile, isTablet] = useIsMobile();
  const airport = process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE;
  const isLinate = airport?.toLowerCase() !== "mxp";

  const [categoryFilter, setCategoryFilter] = useState<null | string>(null);
  const [isClientMounted, setIsClientMounted] = useState(false);

  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const cardList = useMemo(() => {
    return moduleData?.data?.lstCard?.map((item) => item.xconCard.targeturl) || [];
  }, [moduleData, lang]);

  const cardData = useDataCard<{ data: ICommerceCard }[]>({ urls: cardList });
  const cardToRender = useCardMatcher({
    language: lang,
    cardList: cardData,
    isMobile,
    isTablet,
    currentlySettedFilter: categoryFilter,
    backgroundColor: moduleData?.data?.enuBackgroundCta,
  });

  const backgroundColor = moduleData?.data?.enuBackgroundCta === "grigio" ? "bg-lightgrey" : "bg-white";
  const isFlex = moduleData?.data?.enuTypeInfo === "lancio" && " desk:flex desk:gap-7";
  const lblTitle = moduleData?.data ? DOMPurify.sanitize(moduleData.data?.lblTitle?.[lang] ?? "") : "";
  const txbSubtitle = moduleData?.data ? DOMPurify.sanitize(moduleData.data?.txbSubtitle?.[lang] ?? "") : "";
  const enuTitleColor = moduleData?.data ? configColor("text", moduleData.data.enuTitleColor) : "text-black";
  const dotted = moduleData?.data?.flgHatch || false;

  const typeKey = moduleData?.data?.enuTypeInfo || "unknown";
  let typeCfg = typeMaxSlideMap[typeKey] || ({} as any);
  const notes = (moduleData.data?.lblNotes || "").toString();

  const forceSliderByNotes =
    notes === "M04 - Lista Card (Pag. Homepage Parcheggi)" || notes === "Lista card promo - subhome shopping";

  const firstGrpType = cardData?.[0]?.data?.grpType; 
  const isGridGroup = firstGrpType === "grpShopping" || firstGrpType === "grpProduct";


//   if (isMobile && typeKey === "lancio" && notes.includes("Leonardo")) {
//     const hasGrpInfo = firstGrpType === "grpInfo";
//     if (!hasGrpInfo) {
//       typeCfg = { ...typeCfg, mobileSliderEnabled: false };
//       console.log("[M04] Disabilitato Swiper per card lancio Leonardo (non grpInfo)", { notes, grpType: firstGrpType });
//     } else {
//       console.log("[M04] Mantenuto Swiper per card lancio Leonardo grpInfo", { notes, grpType: firstGrpType });
//     }
//   }


  if (typeKey === "lancio" && isMobile) {
    console.log("[M04] DEBUG lancio mobile", {
      notes,
      notesIncludes: notes.includes("Leonardo"),
      shouldBlockSwiper: isMobile && typeKey === "lancio" && notes.includes("Leonardo"),
    });
  }


  const shouldUseSwiper =
    isClientMounted && typeKey !== "info" &&
    !isGridGroup &&
    (((isMobile || isTablet) && typeCfg?.mobileSliderEnabled) ||
      (!isMobile && !isTablet && typeCfg?.desktopSliderEnabled) ||
      forceSliderByNotes) &&
    !!cardToRender?.length;


  console.log("[M04] decision", {
    typeKey,
    grpType: firstGrpType || null,
    isMobile,
    isTablet,
    notes,
    forceSliderByNotes,
    cfg: {
      mobileSliderEnabled: typeCfg?.mobileSliderEnabled,
      desktopSliderEnabled: typeCfg?.desktopSliderEnabled,
      mobileDirection: typeCfg?.mobileDirection,
      desktopDirection: typeCfg?.desktopDirection,
      mobileSlideNumber: typeCfg?.mobileSlideNumber,
      maxSlideDesktop: typeCfg?.maxSlideDesktop,
      freeMode: typeCfg?.freeMode,
    },
    willUseSwiper: shouldUseSwiper,
    cards: cardToRender?.length || 0,
  });

  return (
    <>
      {moduleData?.data?.enuTypeInfo !== "viaggio" ? (
        <div
          className={
            (moduleData.data?.lblNotes === "Wifi - Card" ? "pt-8 pb-6 mt-10" : "py-8 desk:pb-6") +
            " " +
            backgroundColor +
            " " +
            enuTitleColor
          }
        >
          <div className={`max-w-cont-2 mx-auto px-6 w-full mb-4 ${isFlex}`}>
            <div className={`${isFlex ? "desk:w-1/3 " : " "} mb-11 underlineLink`}>
              <h3
                className="text-3xl font-semibold mb-2.5 font-secondary"
                dangerouslySetInnerHTML={{ __html: lblTitle }}
              />
              <h3 className="text-lg" dangerouslySetInnerHTML={{ __html: txbSubtitle }} />
            </div>

            {cardToRender?.length ? (
              shouldUseSwiper ? (
                <Swiper
                  modules={[FreeMode, Keyboard]}
                  keyboard={{ enabled: true }}
                  freeMode={typeCfg.freeMode}
                  slidesOffsetBefore={typeCfg.slidesOffsetBefore}
                  spaceBetween={isMobile || isTablet ? 15 : 30}
                  slidesPerView={isMobile ? typeCfg.mobileSlideNumber : typeCfg.maxSlideDesktop}
                  centeredSlides={isMobile ? typeCfg.mobileCentered : typeCfg.desktopCentered}
                  direction={isMobile || isTablet ? typeCfg.mobileDirection : typeCfg.desktopDirection}
                  className={`custom-carousel ${isFlex ? "desk:w-10/12" : ""}`}
                  onSwiper={() =>
                    console.log("[M04] Swiper mounted", {
                      typeKey,
                      direction: isMobile || isTablet ? typeCfg.mobileDirection : typeCfg.desktopDirection,
                    })
                  }
                >
                  {cardToRender.map((item, index) => (
                    <SwiperSlide key={index} className={`w-auto ${isMobile || isTablet ? "flex justify-center" : ""}`}>
                      <div className="flex">
                        {item}
                        {cardToRender.length !== index + 1 && dotted ? (
                          <div
                            className={`flex items-center ${
                              isMobile ? "justify-center rotate-90 my-4" : ""
                            } translate-x-4`}
                          >
                            <CustomIcon iconName="dots" className="w-4" />
                          </div>
                        ) : null}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <>
                  {firstGrpType === "grpProduct" ? (
                    <div className="grid desk:grid-cols-4 mob:grid-cols-2 tab:grid-cols-3 desk:gap-6 mob:gap-2">
                      {cardToRender.map((item, index) => (
                        <div key={index}>{item}</div>
                      ))}
                    </div>
                  ) : firstGrpType === "grpShopping" ? (
                    <div className={`justify-between`}>
                      {cardToRender.map((item, index) => (
                        <React.Fragment key={index}>
                          <div key={uuid()}>{item}</div>
                          {cardToRender.length !== index + 1 && dotted ? (
                            <div
                              className={`flex items-center ${
                                isMobile ? "justify-center rotate-90 my-4" : ""
                              } translate-x-4`}
                            >
                              <CustomIcon iconName="dots" className="w-4" />
                            </div>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={
                        "flex gap-4 justify-between " +
                        (isMobile
                          ? notes === "M04 LINATE CARD HOMEPAGE"
                            ? "flex-row"
                            : typeKey === "lancio"
                            ? "flex-col"
                            : "flex-col"
                          : "flex-row")
                      }
                    >
                      {cardToRender.map((item, index) => (
                        <React.Fragment key={index}>
                          <div>{item}</div>
                          {cardToRender.length !== index + 1 && dotted ? (
                            <div className={`flex items-center ${isMobile ? "justify-center rotate-90 my-4" : ""}`}>
                              <CustomIcon iconName="dots" className="w-4" />
                            </div>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : null}
          </div>
        </div>
      ) : (
        <div className="max-w-cont-1 px-6 mx-auto w-full">
          <CardList
            currentySettedFilter={categoryFilter}
            onClickFilter={setCategoryFilter}
            cards={cardToRender}
            title={moduleData?.data?.lblTitle || { it: "", en: "" }}
            filters={moduleData?.data?.lstFilters ?? []}
            filterTitle={moduleData?.data?.lblTitleFilter ? moduleData.data.lblTitleFilter[lang] : ""}
          />
        </div>
      )}
    </>
  );
};

export default ModuleFourWrapper;


interface ICardMatcherProps {
  language: string;
  cardList: { data: ICommerceCard }[];
  isMobile?: boolean;
  isTablet?: boolean;
  currentlySettedFilter: string | null;
  backgroundColor: string;
}

const useCardMatcher = ({
  language,
  cardList,
  isMobile = false,
  isTablet = false,
  currentlySettedFilter,
  backgroundColor,
}: ICardMatcherProps) => {
  const { i18n } = useTranslation();
  const lang = language ? language : i18n.language;
  const router = useRouter();
  const [area, setArea] = useState("all");
  const [category, setCategory] = useState("all");
  const [terminalNum, setTerminalNum] = useState("all");
  const setAreaFilter = useSetRecoilState(AreaFilterAtom);
  const setCategoryFilter = useSetRecoilState(CategoryFilterAtom);
  const setTerminalFilter = useSetRecoilState(TerminalFilterAtom);

  useEffect(() => {
    setAreaFilter(area);
    setTerminalFilter(terminalNum);
    setCategoryFilter(category);
  });

  const matchCardType = (card: any) => {
    switch (card.enuTypeInfo) {
      case "info": {
        let infoImagePath =
          card.upiImageInfo?.mediaserverpath && card.upiImageInfo?.resourcename
            ? `${card.upiImageInfo.mediaserverpath}/${card.upiImageInfo.resourcename}`
            : "";
        return !isMobile && !isTablet ? (
          <InfoCard
            language={lang}
            bgColor={backgroundColor === "grigio" ? "bg-white" : "bg-lightgrey"}
            imageUrl={infoImagePath}
            infoItems={card.lstInfo}
            key={card.metainfo.id}
            title={card.lblTitleInfo[lang]}
            description={card.lblSubtitle[lang]}
          />
        ) : (
          <InfoAccordionCard
            language={lang}
            bgColor={backgroundColor === "grigio" ? "bg-white" : "bg-lightgrey"}
            imageUrl={infoImagePath}
            infoItems={card.lstInfo}
            key={card.metainfo.id}
            title={card.lblTitleInfo[lang]}
            description={card.lblSubtitle[lang]}
          />
        );
      }
      case "ecommerce": {
        let ecommerceImagePath =
          card.upiImageEcommerceDesktop[lang]?.mediaserverpath && card.upiImageEcommerceDesktop[lang]?.resourcename
            ? `${card.upiImageEcommerceDesktop[lang].mediaserverpath}/${card.upiImageEcommerceDesktop[lang].resourcename}`
            : "";
        return (
          <a href={getPathByLink(card, lang)} target={card.flgTargetCtaEcommerce ? "_blank" : "_self"}>
            <CommerceCard
              key={card.metainfo.id}
              title={card.lblTitleEcommerce[lang]}
              imageUrl={ecommerceImagePath}
              description={card.lblSubtitle[lang]}
              ctaText={card.lblLabelCtaEcommerce[lang]}
              onClick={() => {}}
            />
          </a>
        );
      }
      case "negozio":
        if (card.grpType === "grpShopping" && card.lblTitleShopping[lang] === "shop")
          return (
            <>
              <ShopFilter setCategory={setCategory} setArea={setArea} setTerminal={setTerminalNum} area={area} />
              <ShopList category={category} area={area} terminalNum={terminalNum} />
            </>
          );
        if (card.grpType === "grpShopping" && card.lblTitleShopping[lang] === "food")
          return (
            <>
              <RestourantFilter setCategory={setCategory} setArea={setArea} setTerminal={setTerminalNum} area={area} />
              <RestourantList category={category} area={area} terminalNum={terminalNum} />
            </>
          );
        if (card.grpType === "grpProduct")
          return (
            <Product
              lblSubtitleShopping={card.lblSubtitleProduct[lang]}
              lblCrossedOutPrice={card.lblCrossedOutPriceProduct[lang]}
              lblLabelCtaShopping={card.lblLabelCtaProduct[lang]}
              lblPriceShopping={card.lblPriceProduct[lang]}
              lblTitleShopping={card.lblTitleProduct[lang]}
              upiImageShoppingDesktop={`${card.upiImageProductDesktop[lang].mediaserverpath}/${card.upiImageProductDesktop[lang].resourcename}`}
            />
          );
      case "luogo":
        return <div key={card.metainfo.id}>Card tipo luogo</div>;
      case "promozione":
        return <div key={card.metainfo.id}>Card tipo promozione</div>;
      case "informativa":
        return <div key={card.metainfo.id}>Card tipo informativa</div>;
      case "lancio": {
        let launchImagePath = "";
        switch (card?.grpImageBox) {
          case "grpImageBoxQ":
            launchImagePath = (isMobile ? card.upiImageBoxQMobile[lang] : card.upiImageBoxQDesktop[lang])
              ?.mediaserverpath
              ? `${(isMobile ? card.upiImageBoxQMobile[lang] : card.upiImageBoxQDesktop[lang]).mediaserverpath}/${
                  (isMobile ? card.upiImageBoxQMobile[lang] : card.upiImageBoxQDesktop[lang]).resourcename
                }`
              : "";
            break;
          case "grpImageBoxH":
            launchImagePath = (isMobile ? card.upiImageBoxHMobile[lang] : card.upiImageBoxHDesktop[lang])
              ?.mediaserverpath
              ? `${(isMobile ? card.upiImageBoxHMobile[lang] : card.upiImageBoxHDesktop[lang]).mediaserverpath}/${
                  (isMobile ? card.upiImageBoxHMobile[lang] : card.upiImageBoxHDesktop[lang]).resourcename
                }`
              : "";
            break;
        }
        if (card.upiImageBoxQMobile[lang]?.mediaserverpath)
          return (
            <a href={getPathByLink(card, lang)} target={card?.flgTarget ? "_blank" : "_self"}>
              <LaunchCard
                isLaunch={true}
                isSquare={true}
                onClick={() => {}}
                imageUrl={launchImagePath}
                title={card.lblTitleBox[lang]}
                txtColor={configColor("text", card?.enuTitleColorBox)}
              />
            </a>
          );
        else return <></>;
      }
      case "lancio con testi":
        return <div key={card.metainfo.id}>Card tipo lancio con testi</div>;
      case "servizio": {
        let serviceImagePath = "";
        if (!isMobile) {
          serviceImagePath =
            card.upiImageServiceDesktop[lang]?.mediaserverpath && card.upiImageServiceDesktop[lang]?.resourcename
              ? `${card.upiImageServiceDesktop[lang].mediaserverpath}/${card.upiImageServiceDesktop[lang].resourcename}`
              : "";
        } else {
          serviceImagePath =
            card.upiImageServiceMobile[lang]?.mediaserverpath && card.upiImageServiceMobile[lang]?.resourcename
              ? `${card.upiImageServiceMobile[lang].mediaserverpath}/${card.upiImageServiceMobile[lang].resourcename}`
              : "";
        }
        return <ServiceCard language={lang} key={card.metainfo.id} data={card} />;
      }
      case "viaggio_natura":
      case "viaggio_cultura":
      case "viaggio_citta":
      case "viaggio_mare": {
        let destinationImagePath = "";
        if (isMobile) {
          destinationImagePath =
            card.upiImageMobile?.mediaserverpath && card.upiImageMobile?.resourcename
              ? `${card.upiImageMobile.mediaserverpath}/${card.upiImageMobile.resourcename}`
              : "";
        } else {
          destinationImagePath =
            card.upiImageDesk?.mediaserverpath && card.upiImageDesk?.resourcename
              ? `${card.upiImageDesk.mediaserverpath}/${card.upiImageDesk.resourcename}`
              : "";
        }
        return (
          <DestinationCard
            key={card.metainfo.id}
            onClickHint={() => null}
            hint={card?.xtaxDestinations?.ti?.[0]?.lblNation?.[lang] ?? ""}
            onClick={() => router.push(getPathByLink(card, lang))}
            title={card?.xtaxDestinations?.ti?.[0]?.lblCityName?.[lang] ?? ""}
            imageUrl={destinationImagePath}
          />
        );
      }
      default:
        return <div key={card.metainfo.id}>Card type not recognized</div>;
    }
  };

  const resultCards = useMemo(
    () =>
      cardList
        ?.filter((cardItem) =>
          currentlySettedFilter ? cardItem.data.enuTypeInfo === currentlySettedFilter : !currentlySettedFilter
        )
        .map((cardItem) => matchCardType(cardItem.data)),
    [cardList, lang, isMobile, currentlySettedFilter]
  );

  return resultCards;
};

interface IGenericCardDTO {
  metainfo: IMetaInfo;
  lblSubtitle: { [key: string]: string };
  grpType: string;
  enuTypeInfo:
    | "info"
    | "ecommerce"
    | "negozio"
    | "luogo"
    | "promozione"
    | "informativa"
    | "lancio"
    | "servizio"
    | "lancio con testi"
    | "viaggio_citta"
    | "viaggio_cultura"
    | "viaggio_natura"
    | "viaggio_mare";
}

interface IInfoCard {
  lblTitleInfo: { [key: string]: string };
}

interface ICommerceCard extends IGenericCardDTO {
  lblNotes: string;
  enuBackgroundCtaEcommerce: string;
  enuSubtitleColorEcommerce: string;
  enuTitleColorEcommerce: string;
  flgTargetCtaEcommerce: boolean;
  lblLabelCtaEcommerce: { [key: string]: string };
  lblLinkCtaEcommerce: { [key: string]: string };
  lblTitleEcommerce: { [key: string]: string };
  upiImageEcommerceDesktop: { [key: string]: IImageInfoDTO };
}